import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useGetMyBankAccountsQuery, useCreateWithdrawalMutation } from '@/store/api/apiSlice';

interface CreateWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableBalance: number;
}

export default function CreateWithdrawalModal({ isOpen, onClose, availableBalance }: CreateWithdrawalModalProps) {
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const { data: bankAccounts = [] } = useGetMyBankAccountsQuery();
  const [createWithdrawal, { isLoading }] = useCreateWithdrawalMutation();

  const minimumWithdrawal = 100;

  useEffect(() => {
    if (isOpen && bankAccounts.length > 0) {
      // Set default bank account
      const defaultAccount = bankAccounts.find(acc => acc.isDefault) || bankAccounts[0];
      if (defaultAccount) {
        setBankAccountId(defaultAccount.id);
      }
    }
  }, [isOpen, bankAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amountNum = parseFloat(amount);

    // Validation
    if (!amountNum || amountNum < minimumWithdrawal) {
      setError(`Minimum withdrawal amount is \u20A6${minimumWithdrawal.toLocaleString()}`);
      return;
    }

    if (amountNum > availableBalance) {
      setError(`Insufficient balance. Available: \u20A6${availableBalance.toLocaleString()}`);
      return;
    }

    if (!bankAccountId) {
      setError('Please select a bank account');
      return;
    }

    try {
      await createWithdrawal({
        amount: amountNum,
        bankAccountId,
        reason: reason || 'Earnings withdrawal',
      }).unwrap();

      // Reset form and close
      setAmount('');
      setReason('');
      setError('');
      onClose();
    } catch (err: any) {
      console.error('Withdrawal error:', err);
      
      // Handle nested error message structure from backend
      let errorMessage = 'Failed to create withdrawal request';
      
      if (err?.data?.message) {
        if (typeof err.data.message === 'object' && err.data.message.message) {
          // Handle nested message structure: { message: { message: [...] } }
          if (Array.isArray(err.data.message.message)) {
            errorMessage = err.data.message.message.join(', ');
          } else {
            errorMessage = err.data.message.message;
          }
        } else if (Array.isArray(err.data.message)) {
          // Handle array of messages
          errorMessage = err.data.message.join(', ');
        } else if (typeof err.data.message === 'string') {
          errorMessage = err.data.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setError('');
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Request Withdrawal</h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Available Balance Display */}
              <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                <p className="text-sm text-green-600 mb-1">Available Balance</p>
                <p className="text-2xl font-bold text-green-900">{formatCurrency(availableBalance)}</p>
              </div>

              {/* Bank Account Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Bank Account <span className="text-red-500">*</span>
                </label>
                <select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="">Select bank account</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bankName} - {account.accountNumber} {account.isDefault && '(Default)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Withdrawal Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    required
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum: {formatCurrency(minimumWithdrawal)} | Maximum: {formatCurrency(availableBalance)}
                </p>
                {availableBalance < minimumWithdrawal && (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    Insufficient balance. You need at least {formatCurrency(minimumWithdrawal)} to withdraw.
                  </p>
                )}
              </div>

              {/* Reason (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Monthly earnings withdrawal"
                  rows={3}
                  maxLength={200}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {reason.length}/200 characters
                </p>
              </div>

              {/* Important Notes */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-900 font-medium mb-2">Important Notes:</p>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Processing typically takes 1-3 business days</li>
                  <li>You can only have one pending withdrawal request at a time</li>
                  <li>Funds will be credited to your selected bank account</li>
                  <li>You'll receive email notifications on status changes</li>
                </ul>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                className="flex-1 px-6 py-2.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !amount || !bankAccountId}
                className="flex-1 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
